import { TImageField, TLinkField, TStringField } from '../../../types/sitecore.types';
import { THeadingTag } from '../../../types/types';
import { getFollowingHTag } from '../../../utils/seo.utils';
import { TColHeadings, TGroupItemFields, TGroupItems, TRowFields, TRowItems, TTableFields, TTableProps } from '../types/index.types';
import { counterArray } from '../utils';

export class TableRowModel {
  private readonly fields: TRowFields | undefined;

  constructor(props: TRowItems) {
    this.fields = props?.fields;
  }

  getCellData(number: number): TStringField {
    return this.fields?.[`cell${number}`];
  }

  get groupName(): TStringField | undefined {
    return this.fields?.groupName;
  }

  get icon(): TImageField | undefined {
    return this.fields?.icon;
  }

  get tooltipTitle(): TStringField | undefined {
    return this.fields?.tooltipTitle;
  }

  get tooltip(): TStringField | undefined {
    return this.fields?.tooltip;
  }

  get title(): TStringField | undefined {
    return this.fields?.title;
  }
}

export class TableGroupModel {
  private readonly fields: TGroupItemFields | undefined;

  constructor(props: TGroupItems) {
    this.fields = props?.fields;
  }

  get title(): TStringField | undefined {
    return this.fields?.title;
  }

  get items(): TableRowModel[] {
    return this.fields?.items?.map((item) => new TableRowModel(item)) || [];
  }
}

export class TableModel {
  private readonly fields: TTableFields | undefined;
  private readonly params: TTableProps['params'] | undefined;

  constructor(props: TTableProps) {
    this.fields = props?.fields;
    this.params = props?.params;
  }

  get colHeadings(): TColHeadings[] {
    const totalColumns = Number(this.fields?.nOfVisibleColumn?.value);
    if (totalColumns) {
      return counterArray(totalColumns).map((num: number) => {
        return {
          text: this.getColumnTextData(num),
          image: this.getColumnImageData(num),
        };
      });
    }
    return [];
  }

  getColumnTextData(number: string | number): TStringField {
    return this.fields?.[`column${number}Text`];
  }

  getColumnImageData(number: string | number): TImageField {
    return this.fields?.[`column${number}Image`];
  }

  get items(): Array<TableGroupModel | TableRowModel> {
    const items = this.fields?.items?.map((item) => {
      if ((item.fields as TGroupItemFields)?.templateId === '{467E4AEE-FF09-45E2-81F4-EE28518E8559}') {
        return new TableGroupModel(item);
      } else {
        return new TableRowModel(item);
      }
    });
    return items || [];
  }

  get isEmptyFirstColumn(): boolean {
    return this.items.every((item) => {
      if (item instanceof TableGroupModel) {
        return item.items.every((rowItem) => {
          return !rowItem.title?.value?.length;
        });
      }
      if (item instanceof TableRowModel) {
        return !item.title?.value?.length;
      }
    });
  }

  get groupItemsIndexes(): number[] {
    const newExpandedRows: number[] = [];
    this.items.forEach((item, index) => {
      if (item instanceof TableGroupModel) {
        newExpandedRows.push(index);
      }
    });
    return newExpandedRows;
  }

  get nOfVisibleColumn(): number {
    return Number(this.fields?.nOfVisibleColumn?.value);
  }

  get disableExpand(): boolean {
    return this.fields?.disableExpand?.value || false;
  }

  get disableSearch(): boolean {
    // feature not part of V1
    return true;
    // return this.fields?.disableSearch?.value || false;
  }

  get disablePrint(): boolean {
    // feature not part of V1
    return true;
    // return this.fields?.disablePrint?.value || false;
  }

  get disableExcel(): boolean {
    // feature not part of V1
    return true;
    // return this.fields?.disableExcel?.value || false;
  }

  get hideRowsHeader(): boolean {
    // feature not part of V1
    return false;
    // return this.fields?.hideRowsHeader?.value || false;
  }

  get hideColumnHeader(): boolean {
    // feature not part of V1
    return false;
    // return this.fields?.hideColumnHeader?.value || false;
  }

  get stickyHeadingRow(): boolean {
    return this.fields?.stickyHeadingRow?.value || false;
  }

  get stickyHeadingColumn(): boolean {
    return this.fields?.stickyHeadingColumn?.value || false;
  }

  get heading(): TStringField | undefined {
    return this.fields?.heading;
  }

  get link(): TLinkField | undefined {
    return this.fields?.link;
  }

  get eyebrow(): TStringField | undefined {
    return this.fields?.eyebrow;
  }

  get headingTag(): THeadingTag {
    return (this.params?.headingTag?.toLowerCase() as THeadingTag) || 'h2';
  }

  get groupHeadingTag(): THeadingTag {
    return getFollowingHTag(this.headingTag);
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }
}
